<template>
  <div class="task-message-box">
    <el-popover
      v-model="visible"
      placement="bottom-end"
      width="420"
      trigger="click"
      @show="fetchNotifications"
    >
      <div class="task-message-header">
        <span>任务消息</span>
        <el-button type="text" @click="refreshMessages">刷新</el-button>
      </div>
      <div v-loading="loading">
        <div v-if="notifications.length" class="task-message-list">
          <div
            v-for="item in notifications"
            :key="item._id"
            class="task-message-item"
            @click="handleNotificationClick(item)"
          >
            <div class="task-message-item-top">
              <span class="task-message-title">{{ item.title }}</span>
              <el-tag size="mini" :type="notificationTagType(item.status)">
                {{ notificationStatusText(item.status) }}
              </el-tag>
            </div>
            <div class="task-message-content">{{ item.content }}</div>
            <div class="task-message-time">
              {{ formatTime(item.createdAt || item.deliveredAt) }}
            </div>
          </div>
        </div>
        <div v-else class="task-message-empty">暂无消息</div>
      </div>
      <div slot="reference" class="task-message-reference">
        <el-badge :value="unreadCount" :hidden="!unreadCount">
          <div class="task-message-trigger">
            <i class="el-icon-bell"></i>
          </div>
        </el-badge>
      </div>
    </el-popover>

    <el-dialog
      :title="taskDialogTitle"
      :visible.sync="detailVisible"
      width="560px"
      append-to-body
    >
      <div v-loading="detailLoading" class="task-detail-wrap" v-if="currentTask">
        <div class="task-detail-row">
          <span>客户名称</span>
          <strong>{{ currentTask.customerName || "-" }}</strong>
        </div>
        <div class="task-detail-row">
          <span>接收人</span>
          <strong>{{ currentTask.receiverName || "-" }}</strong>
        </div>
        <div class="task-detail-row">
          <span>负责人</span>
          <strong>{{ currentTask.ownerName || "暂无" }}</strong>
        </div>
        <div class="task-detail-row">
          <span>计划完成时间</span>
          <strong>{{ formatTime(currentTask.plannedAt) }}</strong>
        </div>
        <div class="task-detail-row">
          <span>最后联系时间</span>
          <strong>{{ formatTime(currentTask.lastContactTime, "暂无") }}</strong>
        </div>
        <div class="task-detail-row">
          <span>任务状态</span>
          <el-tag :type="taskTagType(currentTask)">
            {{ taskStatusText(currentTask.status, currentTask.isOverdue) }}
          </el-tag>
        </div>
        <div class="task-detail-content">
          <div class="task-detail-label">任务内容</div>
          <div class="task-detail-text">{{ currentTask.content || "-" }}</div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button
          v-if="currentTask && currentTask.canOperate && currentTask.status === 'pending'"
          type="primary"
          :loading="statusLoading"
          @click="updateTaskStatus('processing')"
        >
          开始处理
        </el-button>
        <el-button
          v-if="
            currentTask &&
            currentTask.canOperate &&
            ['pending', 'processing'].includes(currentTask.status)
          "
          type="success"
          :loading="statusLoading"
          @click="updateTaskStatus('completed')"
        >
          标记完成
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      visible: false,
      unreadCount: 0,
      notifications: [],
      loading: false,
      detailVisible: false,
      detailLoading: false,
      statusLoading: false,
      currentTask: null,
      currentTaskType: "",
      eventSource: null,
      streamStarted: false,
    };
  },
  computed: {
    taskDialogTitle() {
      if (this.currentTaskType === "活动触达") {
        return "活动任务";
      }
      return this.currentTaskType === "MARKETING" ? "营销任务" : "客户建联任务";
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(value) {
        if (value && value._id) {
          this.bootstrap();
        }
      },
    },
  },
  beforeDestroy() {
    this.closeStream();
  },
  methods: {
    async bootstrap() {
      if (this.streamStarted) {
        return;
      }
      this.streamStarted = true;
      await this.fetchUnreadCount();
      await this.fetchNotifications();
      this.createStream();
    },
    createStream() {
      if (this.eventSource || !localStorage.token) {
        return;
      }
      const streamUrl = `${process.env.VUE_APP_API_URL}/notification/stream?token=${localStorage.token}`;
      this.eventSource = new EventSource(streamUrl);
      this.eventSource.addEventListener("notification.created", () => {
        this.refreshMessages();
        this.dispatchRealtimeEvent("notification.created");
      });
      this.eventSource.addEventListener("notification.read", () => {
        this.fetchUnreadCount();
        this.fetchNotifications();
      });
      this.eventSource.addEventListener("task.progress.changed", () => {
        this.dispatchRealtimeEvent("task.progress.changed");
      });
      this.eventSource.addEventListener("task.status.changed", () => {
        this.fetchNotifications();
        this.dispatchRealtimeEvent("task.status.changed");
      });
      this.eventSource.onerror = () => {
        this.closeStream();
        this.streamStarted = false;
        setTimeout(() => {
          if (this.user && this.user._id) {
            this.bootstrap();
          }
        }, 3000);
      };
    },
    closeStream() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },
    dispatchRealtimeEvent(type) {
      window.dispatchEvent(
        new CustomEvent("customer-link-task-realtime", {
          detail: {
            type,
          },
        })
      );
    },
    getTaskEndpoint(taskType) {
      if (taskType === "活动触达") {
        return "task";
      }
      return taskType === "MARKETING" ? "v2/marketing-tasks" : "customer-link-tasks";
    },
    async refreshMessages() {
      await Promise.all([this.fetchUnreadCount(), this.fetchNotifications()]);
    },
    async fetchUnreadCount() {
      if (!this.user || !this.user._id) {
        return;
      }
      const { data } = await this.$http.get("notification/unread");
      this.unreadCount = data.count || 0;
    },
    async fetchNotifications() {
      if (!this.user || !this.user._id) {
        return;
      }
      this.loading = true;
      try {
        const { data } = await this.$http.get("notification", {
          params: {
            limit: 20,
            page: 1,
          },
        });
        this.notifications = data.data || [];
      } finally {
        this.loading = false;
      }
    },
    formatTime(value, emptyText = "-") {
      if (!value) {
        return emptyText;
      }
      return this.dayjs(value).format("YYYY-MM-DD HH:mm");
    },
    notificationStatusText(status) {
      return {
        unread: "未读",
        read: "已读",
        processed: "已处理",
      }[status] || "未读";
    },
    notificationTagType(status) {
      return {
        unread: "danger",
        read: "warning",
        processed: "success",
      }[status] || "info";
    },
    taskStatusText(status, isOverdue) {
      if (status === "completed") {
        return "已完成";
      }
      if (isOverdue) {
        return "已逾期";
      }
      return {
        pending: "待处理",
        processing: "进行中",
      }[status] || "待处理";
    },
    taskTagType(task) {
      if (!task) {
        return "info";
      }
      if (task.status === "completed") {
        return "success";
      }
      if (task.isOverdue) {
        return "danger";
      }
      if (task.status === "processing") {
        return "warning";
      }
      return "info";
    },
    async handleNotificationClick(item) {
      if (item.status === "unread") {
        await this.$http.patch(`notification/${item._id}/read`);
        item.status = "read";
        this.unreadCount = Math.max(this.unreadCount - 1, 0);
      }
      if (item.redirectUrl) {
        try {
          this.$router.push(item.redirectUrl);
        } catch (error) {}
      }
      await this.openTaskDetail(item.taskId, item.taskType);
      this.visible = false;
    },
    async openTaskDetail(taskId, taskType) {
      if (!taskId) {
        return;
      }
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const endpoint = this.getTaskEndpoint(taskType);
        const { data } = await this.$http.get(`${endpoint}/${taskId}`);
        this.currentTaskType = data.type || taskType || "";
        this.currentTask = data;
      } finally {
        this.detailLoading = false;
      }
    },
    async updateTaskStatus(status) {
      if (!this.currentTask) {
        return;
      }
      this.statusLoading = true;
      try {
        const endpoint = this.getTaskEndpoint(this.currentTaskType || this.currentTask.type);
        const { data } = await this.$http.patch(
          `${endpoint}/${this.currentTask._id}/status`,
          {
            status,
          }
        );
        this.currentTaskType = data.type || this.currentTaskType;
        this.currentTask = data;
        this.$message.success(status === "completed" ? "任务已完成" : "任务已开始处理");
        await this.refreshMessages();
        this.dispatchRealtimeEvent("task.status.changed");
      } finally {
        this.statusLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.task-message-reference {
  margin-right: 16px;
}

.task-message-trigger {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #409eff;
  cursor: pointer;
  font-size: 18px;
}

.task-message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.task-message-list {
  max-height: 420px;
  overflow-y: auto;
}

.task-message-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
}

.task-message-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.task-message-title {
  color: #303133;
  font-weight: 600;
}

.task-message-content {
  color: #606266;
  line-height: 20px;
  margin-bottom: 8px;
}

.task-message-time {
  color: #909399;
  font-size: 12px;
}

.task-message-empty {
  text-align: center;
  color: #909399;
  padding: 32px 0;
}

.task-detail-wrap {
  color: #303133;
}

.task-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.task-detail-row span {
  color: #909399;
}

.task-detail-content {
  margin-top: 20px;
}

.task-detail-label {
  color: #909399;
  margin-bottom: 8px;
}

.task-detail-text {
  background: #f7f9fc;
  border-radius: 8px;
  padding: 14px;
  white-space: pre-wrap;
  line-height: 22px;
}
</style>
